import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import {
  ApiGuarantee,
  ApiPackage,
  ApiProductCatalogItem,
  ApiProductDescriptionItem,
} from '../../../../core/models/product-api.models';
import { ProductApiService } from '../../../../core/services/product-api.service';

@Component({
  selector: 'app-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrls: ['./product-detail.component.scss'],
})
export class ProductDetailComponent implements OnDestroy {
  product?: ApiProductCatalogItem;
  selectedTabIndex = 0;
  loading = false;
  error: string | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly productApi: ProductApiService,
  ) {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id') ?? '';
      this.selectedTabIndex = 0;
      this.loadProduct(id);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get imageUrl(): string {
    return this.productApi.optimizeImageUrl(this.product?.image?.url, {
      width: 1600,
      height: 900,
    });
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/products/product-auto.jpg';
  }

  getDescriptions(product: ApiProductCatalogItem): ApiProductDescriptionItem[] {
    return [...(product.descriptions ?? [])].sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));
  }

  getPackages(product: ApiProductCatalogItem): ApiPackage[] {
    return product.pakages ?? [];
  }

  getGuarantees(pkg: ApiPackage) {
    return (pkg.guaranteePakages ?? [])
      .map((item) => item.guarantee)
      .filter((guarantee): guarantee is NonNullable<typeof guarantee> => !!guarantee);
  }

  sentenceCase(value: string | null | undefined): string {
    const trimmed = (value ?? '').trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  garantieContent(guarantee: ApiGuarantee): {
    rows: Array<{ mise: string; capital: string }>;
    notes: string[];
  } {
    return this.parseMiseCapital(guarantee.description ?? '');
  }

  private parseMiseCapital(raw: string): {
    rows: Array<{ mise: string; capital: string }>;
    notes: string[];
  } {
    const text = raw.replace(/<[^>]+>/g, '\n').replace(/&nbsp;/g, ' ');
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.replace(/\s+/g, ' ').trim())
      .filter(Boolean);

    const rows: Array<{ mise: string; capital: string }> = [];
    const notes: string[] = [];

    for (const line of lines) {
      if (/mise/i.test(line) && /capital/i.test(line)) {
        continue;
      }
      if (/^[_—\-]+$/.test(line)) {
        continue;
      }

      const colon = line.match(/^(.+?)\s*:\s*(.+)$/);
      if (colon) {
        const left = colon[1].trim();
        const right = colon[2].trim();
        if (this.isAmount(left) && this.isAmount(right)) {
          rows.push({ mise: left, capital: right });
          continue;
        }
      }

      const amounts = line.match(/(\d[\d\s.,]*\s*XAF)/gi);
      if (amounts && amounts.length >= 2) {
        rows.push({ mise: amounts[0].trim(), capital: amounts[1].trim() });
        continue;
      }

      notes.push(line);
    }

    return { rows, notes };
  }

  private isAmount(value: string): boolean {
    return /\d/.test(value) && /xaf/i.test(value);
  }

  private loadProduct(id: string): void {
    if (!id) {
      this.product = undefined;
      this.error = 'Identifiant produit manquant.';
      return;
    }

    this.loading = true;
    this.error = null;
    this.product = undefined;

    this.productApi
      .getById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (product) => {
          this.product = product;
          this.loading = false;
          if (!product) {
            this.error = 'Produit introuvable.';
          }
        },
        error: (err) => {
          console.error('[ProductDetail] load failed', err);
          this.loading = false;
          this.error = 'Impossible de charger ce produit.';
        },
      });
  }
}
